'use client';

import { c as _c } from "react/compiler-runtime";
import { isServer, QueryClient, QueryClientProvider as TanstackQueryClientProvider } from '@tanstack/react-query';
import dynamic from 'next/dynamic';
import { type ReactNode, Suspense } from 'react';
function makeQueryClient() {
  return new QueryClient({
    defaultOptions: {
      queries: {
        // With SSR, we usually want to set some default staleTime
        // above 0 to avoid refetching immediately on the client
        staleTime: 60 * 1000
      }
    }
  });
}
let browserQueryClient: QueryClient | undefined = undefined;
function getQueryClient() {
  if (isServer) {
    // Server: always make a new query client
    return makeQueryClient();
  } else {
    // Browser: make a new query client if we don't already have one
    // This is very important, so we don't re-make a new client if React
    // suspends during the initial render. This may not be needed if we
    // have a suspense boundary BELOW the creation of the query client
    browserQueryClient ??= makeQueryClient();
    return browserQueryClient;
  }
}
const ConditionalDevTools = process.env.NODE_ENV === 'development' ? dynamic(async () => (await import('./dev-tools')).DevTools, {
  ssr: false
}) : () => null;
function QueryClientProvider(t0) {
  const $ = _c(4);
  const {
    children
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = getQueryClient();
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const queryClient = t1;
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <Suspense><ConditionalDevTools /></Suspense>;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] !== children) {
    t3 = <TanstackQueryClientProvider client={queryClient}>{children}{t2}</TanstackQueryClientProvider>;
    $[2] = children;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  return t3;
}
export { QueryClientProvider };